/**
 * Canvas Renderer
 * Main rendering engine for Canvas-based Viewer mode
 * Converts JointJS graphJson to Canvas drawings
 */

import type { dia } from '@joint/core'
import { ViewportManager } from './ViewportManager'
import { renderSVGPath } from './SVGPathParser'
import { resolveColor } from './colorTokens'
import { planElementRender } from './renderPlan'
import { cellsToCanvasElements, type CellLike } from './cellsToCanvasElements'
import { hitTestElements } from './hitTest'
import { buildRelationshipSegments, type RelationshipSegment } from './relationshipSegments'
import {
  collectRelationshipLinesFromEntities,
  type RelationshipEntity,
} from '../../../map-editor-pages/editor/lib/relationshipCollect'
import type { TemplateRelationType } from '../../../map-editor-entities/schema/templateSchema'
import type { CanvasElement, RenderOptions, Transform } from './types'
import { routeLayerToCategory } from '../../../map-editor-shared/lib/layerUtils'
import type {
  CctvStatus,
  ParkingLocationStatus,
  LightingStatus,
} from '../../status/types/status'
import { log, LogCategory } from '../../../map-editor-shared/lib/logger'

/** 실시간 상태 묶음 — StatusOverlay 가 읽던 statusStore 조각과 동일. */
export interface CanvasStatusInput {
  floorId?: string | null
  parkingStatuses?: Record<string, ParkingLocationStatus>
  cctvStatuses?: Record<string, CctvStatus>
  lightingStatuses?: Record<string, LightingStatus>
  lightingBaseline?: boolean
  /** 비상 호출 중인 CCTV 이름 — 이 bloom 은 lightType 무관 점멸. */
  emergencyCctvs?: string[]
  /** 비상 호출 CCTV 에 연결된 조명 셀 id — 해당 조명 도형이 점멸한다. */
  emergencyLightIds?: string[]
}

/** 유휴(절전) 통로등 기본 디밍 — StatusOverlay.BASELINE_DIM 과 동일. */
const BASELINE_DIM = 20

/** 조명 종류(레이블) → glow RGB. StatusOverlay.lightGlowRgb 와 동일. */
function lightGlowRgb(lightType: string): [number, number, number] {
  if (lightType.includes('경광')) return [239, 68, 68] // warning — red
  if (lightType.includes('충돌')) return [249, 115, 22] // caution — orange
  if (lightType.includes('만공차')) return [52, 211, 153] // parking — green
  return [255, 214, 10] // aisle/dimming(통로등) — warm yellow
}

/** 이름 정규화 — 맵 CCTV명과 이벤트 cctvName 의 대소문자/공백 차이 흡수. */
function normalizeName(name: string): string {
  return name.trim().toLowerCase()
}

/** data.layer 우선, data.type 폴백으로 카테고리 판정(StatusOverlay.elementCategory 동일). */
function elementCategory(el: CanvasElement): string {
  const layer = el.data?.layer
  if (typeof layer === 'string' && layer) {
    const c = routeLayerToCategory(layer)
    if (c !== 'unknown') return c
  }
  const t = (el.data as { type?: unknown }).type
  if (typeof t === 'string' && t) return routeLayerToCategory(t)
  return 'unknown'
}

function isCctvElement(el: CanvasElement): boolean {
  const c = elementCategory(el)
  if (c === 'cctv' || c === 'warningCctv') return true
  const id = el.id
  return id.includes('cctv') || id.includes('onepassreader') || id.includes('reader')
}

function isParkingElement(el: CanvasElement): boolean {
  if (elementCategory(el) === 'parkingLocation') return true
  return el.id.includes('parking')
}

/** 비상벨 셀 판정 — 시뮬레이터 mapAssetFix 의 isBellCellRaw 와 동일 규칙. */
function isBellElement(el: CanvasElement): boolean {
  const layer = el.data?.layer
  const t = (el.data as { type?: unknown }).type
  return (
    (typeof layer === 'string' && /emergency|bell/i.test(layer)) ||
    (typeof t === 'string' && /emergency|bell/i.test(t))
  )
}

// 라벨 가독성 기준(화면 px). 라벨 폰트는 월드 좌표라 축소 줌에서 1px 미만으로
// 사라지므로 화면 기준 최소 크기로 클램프하고, 디바이스 이름은 아이콘이 이보다
// 작아지는 줌에서는 생략한다(수백 개가 겹쳐 뭉개지는 것 방지).
/** 캔버스 배경 토큰 — 라벨 대비 판정에 쓴다(실제 칠은 CSS 가 한다). */
const CANVAS_BG_TOKEN = 'var(--color-canvas-bg)'

const MIN_LABEL_SCREEN_PX = 11
const MIN_DEVICE_ICON_SCREEN_PX = 12

/**
 * 캔버스 배경에서 읽을 수 없는 어두운 색인지(라벨 대비 판정과 배경 밝기 판정에 함께 쓴다).
 * resolveColor 결과(rgb()/hex) 기준 상대 휘도로 판정 — 미해석 문자열은 안전하게
 * 어두움으로 간주하지 않는다(named color 등은 resolveColor 가 rgb 로 바꿔줌).
 */
function isDarkColor(color: string): boolean {
  let r = -1
  let g = -1
  let b = -1
  const rgb = /^rgba?\((\d+)[,\s]+(\d+)[,\s]+(\d+)/.exec(color)
  if (rgb) {
    r = +rgb[1]; g = +rgb[2]; b = +rgb[3]
  } else {
    const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color)
    if (hex) {
      const h = hex[1].length === 3 ? hex[1].split('').map(c => c + c).join('') : hex[1]
      r = parseInt(h.slice(0, 2), 16); g = parseInt(h.slice(2, 4), 16); b = parseInt(h.slice(4, 6), 16)
    }
  }
  if (r < 0) return false
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 70
}

/** 식별 이름: data.properties.name → label/text → id (StatusOverlay/비상벨과 동일 규칙). */
function elementName(el: CanvasElement): string {
  const props = (el.data as { properties?: { name?: unknown } }).properties
  if (props?.name) return String(props.name)
  const t = el.attrs.label?.text
  if (t) return String(t)
  return el.id
}

/** 자동 관계 생성 직후 5초간 보여 주는 결과 표시(연결선 + 연결된 타겟 강조). */
export interface AutoLinkResultOverlay {
  /** 방금 연결된 타겟 cell id — 주황 실선으로 강조. */
  linkedIds: string[]
  /** 소스 중심 → 타겟 중심 연결선(world 좌표), 관계별 색. */
  lines: { x1: number; y1: number; x2: number; y2: number; color: string }[]
}

/** 자동 관계 미리보기 — 적용 전 후보 연결선과 반경 원(world 좌표). */
export interface AutoLinkPreviewOverlay {
  lines: { x1: number; y1: number; x2: number; y2: number; color: string }[]
  circles: { x: number; y: number; radius: number; color: string }[]
}

/** 도면 대조 표시 — 도면에만 있는 자리·맵에만 있는 객체(world 좌표). */
export interface DrawingCheckMarker {
  x: number
  y: number
  color: string
}

export class CanvasRenderer {
  private canvas: HTMLCanvasElement
  private ctx: CanvasRenderingContext2D
  private viewportManager: ViewportManager
  private elements: CanvasElement[] = []
  private options: Required<RenderOptions>
  private animationFrameId: number | null = null
  private needsRender = false
  private destroyed = false
  // Decoded-image cache keyed by href. Images load async; on load we re-render
  // so icons appear once decoded instead of creating a new Image() each frame.
  private imageCache: Map<string, HTMLImageElement> = new Map()
  // Live graph binding (viewer mode): when set, cell add/remove/change re-syncs
  // elements from the graph model and re-renders, coalesced to one pass/frame.
  private boundGraph: dia.Graph | null = null
  private graphChangeHandler: (() => void) | null = null
  private graphReloadRaf: number | null = null
  // Currently selected cell ids (highlighted on top of the scene).
  private selectedIds: Set<string> = new Set()
  // Relationship edit mode (editor overlay): clickable target ids, drawn with a
  // green outline so the user can see what's linkable. Empty = not editing.
  private editTargetIds: Set<string> = new Set()
  // Relationship overlay: template relation config + cached world-space segments
  // for the current selection (recomputed on selection/elements/config change,
  // NOT on pan/zoom).
  private relationTypes: Record<string, TemplateRelationType> | undefined
  private relationshipSegments: RelationshipSegment[] = []
  // 실시간 상태(주차 점유 색·CCTV 끊김·조명 bloom). 비어 있으면 아무 효과 없음.
  private floorId: string | null = null
  private parkingStatuses: Record<string, ParkingLocationStatus> = {}
  private cctvStatuses: Record<string, CctvStatus> = {}
  private lightingStatuses: Record<string, LightingStatus> = {}
  private lightingByNormalizedName: Record<string, LightingStatus> = {}
  private lightingBaseline = false
  // 비상 호출 중인 CCTV 이름(정규화 포함) → 해당 bloom 을 lightType 무관 점멸.
  private emergencyCctvs: Set<string> = new Set()
  private emergencyLightIds: Set<string> = new Set()
  // 만공차 체크 탭에서 점유 표시할 주차면 cell id 집합 → 빨간색 채움.
  private occupiedParking: Set<string> = new Set()
  // 포커스 지점(world 좌표) — 시뮬레이터에서 선택된 객체 위에 펄스 링을 그린다.
  private focusPoint: { x: number; y: number } | null = null
  // 자동 관계 생성(에디터) 오버레이. SVG 모드의 .unlinked-target / .autolink-connected
  // 클래스와 링크 셀을 캔버스에서 대체한다. 미리보기(반경 밖 타겟)와 생성 결과는
  // 서로 다른 시점에 켜지고 꺼지므로 상태를 분리해 둔다.
  private unlinkedTargetIds: Set<string> = new Set()
  private autoLinkResult: AutoLinkResultOverlay | null = null
  private autoLinkPreview: AutoLinkPreviewOverlay | null = null
  // 맵 에디터 CCTV 관계(좌/우 이웃) 선 — 관계 패널이 열려 있는 동안만 채워진다.
  private cctvRelationLines: AutoLinkPreviewOverlay['lines'] = []
  // 도면 대조 패널이 열려 있는 동안만 채워진다.
  private drawingCheckMarkers: DrawingCheckMarker[] = []
  /** 현재 테마의 캔버스 배경이 어두운가 — 라벨 색/halo 를 뒤집는 기준. */
  private bgIsDark = true
  // 라벨 표시 토글 — CCTV 이름(기본 ON) / 주차면 이름(기본 OFF).
  private showCctvNames = true
  private showParkingNames = false
  // CCTV↔주차면 연결선 표시(기본 ON) + 캐시된 world 좌표 세그먼트.
  // linkedParkingLocationIds 기반이라 selection 과 무관하게 항상 그린다.
  private showCctvLinks = true
  private cctvParkingLinkSegments: { x1: number; y1: number; x2: number; y2: number }[] = []
  // 비상(경광등) bloom 이 화면에 그려졌는지 — true 면 render() 가 다음 프레임을 다시
  // 예약해 점멸 애니메이션을 지속한다(경광등이 꺼지면 자동으로 루프 종료).
  private warningActive = false
  // 사용자가 직접 줌/팬 하기 전까지는 resize 마다 콘텐츠를 다시 맞춘다. ResizeObserver
  // 는 레이아웃 정착 중 중간 크기로 여러 번 발화하므로, "첫 resize 1회"로 잠그면
  // 잘못된 중간 크기에 고정될 수 있다(맵이 점처럼 작게 보이던 원인). 사용자가
  // 줌/팬 하면 true 가 되어 그 뒤 리사이즈는 뷰포트를 보존한다.
  private userInteracted = false

  constructor(canvas: HTMLCanvasElement, options: RenderOptions = {}) {
    this.canvas = canvas
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      throw new Error('Canvas 2D context not available')
    }
    this.ctx = ctx

    this.options = {
      enableCulling: options.enableCulling ?? true,
      enableDirtyRect: options.enableDirtyRect ?? false,
      enableOffscreen: options.enableOffscreen ?? false,
      debug: options.debug ?? false,
    }

    this.viewportManager = new ViewportManager(canvas.width, canvas.height)

    log.info(LogCategory.CANVAS, 'Initialized', { width: canvas.width, height: canvas.height, options: this.options })
  }

  /**
   * Load elements from a static JointJS graphJson (saved floor data).
   * Fits content to view. For a live, editable graph use bindToGraph().
   */
  loadFromGraphJson(graphJson: { cells?: CellLike[] }): void {
    log.info(LogCategory.CANVAS, 'Loading graph...', { cells: graphJson.cells?.length || 0 })

    this.elements = cellsToCanvasElements(graphJson.cells || [])

    log.info(LogCategory.CANVAS, `Loaded elements: ${this.elements.length}`)

    this.recomputeRelationships()
    this.recomputeCctvParkingLinks()
    // 새 데이터 → 자동 fit 재허용(사용자 조작 플래그 리셋).
    this.userInteracted = false
    this.fitToContent()
    this.requestRender()
  }

  /**
   * Bind to a live dia.Graph: render its current cells and re-sync whenever a
   * cell is added, removed, or changed (the persistence path every editing
   * feature already uses). Initial bind fits content; subsequent change-driven
   * reloads preserve the current pan/zoom so applying a mapping doesn't yank the
   * viewport. Replaces any previous binding.
   */
  bindToGraph(graph: dia.Graph): void {
    this.unbindFromGraph()

    this.boundGraph = graph
    this.graphChangeHandler = () => this.scheduleGraphReload()
    // Space-separated event list: element add/remove and any attribute change
    // (position/size/attrs/data), plus reset for graph.clear()/fromJSON().
    graph.on('add remove change reset', this.graphChangeHandler)

    this.reloadFromGraph(true)
  }

  /** Detach the live-graph binding and cancel any pending reload. */
  unbindFromGraph(): void {
    if (this.boundGraph && this.graphChangeHandler) {
      this.boundGraph.off('add remove change reset', this.graphChangeHandler)
    }
    this.boundGraph = null
    this.graphChangeHandler = null
    if (this.graphReloadRaf !== null) {
      cancelAnimationFrame(this.graphReloadRaf)
      this.graphReloadRaf = null
    }
  }

  /** Coalesce bursts of graph mutations into a single reload per frame. */
  private scheduleGraphReload(): void {
    if (this.graphReloadRaf !== null || this.destroyed) return
    this.graphReloadRaf = requestAnimationFrame(() => {
      this.graphReloadRaf = null
      this.reloadFromGraph(false)
    })
  }

  /** Rebuild elements from the bound graph's current cells. */
  private reloadFromGraph(fit: boolean): void {
    if (!this.boundGraph) return
    const cells = this.boundGraph.getCells().map(cell => cell.attributes as CellLike)
    this.elements = cellsToCanvasElements(cells)
    this.recomputeRelationships()
    this.recomputeCctvParkingLinks()
    if (fit) this.fitToContent()
    this.requestRender()
  }

  /**
   * Fit the current elements into the viewport. Public so the wrapper can re-fit
   * once the canvas has its real (post-layout) size — the initial load fits
   * against the canvas's default 300×150 before ResizeObserver runs.
   * minScale 0.01: normalize 로 매우 커진 층(예: ~17000px)도 화면에 들어오게.
   */
  fitToContent(): void {
    const bounds = this.viewportManager.calculateBounds(this.elements)
    if (bounds) {
      this.viewportManager.fitContentToView(bounds, {
        padding: 30,
        maxScale: 4,
        minScale: 0.01,
      })
    }
  }

  /** Current viewport scale (for the wrapper's wheel-zoom factor math). */
  getScale(): number {
    return this.viewportManager.getTransform().scale
  }

  /** Convert canvas-relative (screen) coords to world coords. */
  screenToWorld(screenX: number, screenY: number): { x: number; y: number } {
    return this.viewportManager.screenToWorld(screenX, screenY)
  }

  /** Convert world coords to canvas-relative (screen) coords. */
  worldToScreen(worldX: number, worldY: number): { x: number; y: number } {
    return this.viewportManager.worldToScreen(worldX, worldY)
  }

  /** 상태 키 네임스페이스(주차/CCTV 는 `${floorId}::${id}`)용 floorId. */
  setFloorId(floorId: string | null): void {
    this.floorId = floorId
    this.requestRender()
  }

  /** 실시간 상태를 주입하고 다시 그린다(StatusOverlay 의 in-canvas 대체). */
  setStatus(input: CanvasStatusInput): void {
    if (input.floorId !== undefined) this.floorId = input.floorId
    this.parkingStatuses = input.parkingStatuses ?? {}
    this.cctvStatuses = input.cctvStatuses ?? {}
    this.lightingStatuses = input.lightingStatuses ?? {}
    this.lightingBaseline = input.lightingBaseline ?? false
    this.lightingByNormalizedName = {}
    for (const ls of Object.values(this.lightingStatuses)) {
      this.lightingByNormalizedName[normalizeName(ls.cctvName)] = ls
    }
    // 비상 CCTV 는 원본 이름과 정규화 이름 둘 다 담아 매칭 누락을 막는다.
    this.emergencyCctvs = new Set()
    for (const n of input.emergencyCctvs ?? []) {
      this.emergencyCctvs.add(n)
      this.emergencyCctvs.add(normalizeName(n))
    }
    this.emergencyLightIds = new Set(input.emergencyLightIds ?? [])
    this.requestRender()
  }

  /** 클릭된 셀의 식별 이름(비상벨 매칭용). 없으면 null. */
  getElementName(id: string): string | null {
    const el = this.elements.find(e => e.id === id)
    return el ? elementName(el) : null
  }

  /** 해당 셀이 CCTV(조명/단방향)인지 — 맵 클릭으로 CCTV 선택 시 사용. */
  isCctv(id: string): boolean {
    const el = this.elements.find(e => e.id === id)
    return el ? isCctvElement(el) : false
  }

  /** 해당 셀이 주차면인지 — 맵 클릭으로 주차면 점유 토글 시 사용. */
  isParking(id: string): boolean {
    const el = this.elements.find(e => e.id === id)
    return el ? isParkingElement(el) : false
  }

  /**
   * 상호작용용 픽킹 — 클릭 지점을 포함하는 셀 중 CCTV > 주차면 > 최상위 순으로
   * 우선 선택한다. bbox 기준 단순 hitTest 는 주차면 위에 겹친 조명선/구역(z 높음)을
   * 먼저 잡아 주차면 클릭이 안 됐기 때문(만공차 체크의 주차면 점유 토글에 필요).
   */
  pickInteractive(
    screenX: number,
    screenY: number,
  ): { id: string; name: string | null; isCctv: boolean; isParking: boolean } | null {
    const world = this.viewportManager.screenToWorld(screenX, screenY)
    let topmost: CanvasElement | null = null
    let cctv: CanvasElement | null = null
    let parking: CanvasElement | null = null
    for (let i = this.elements.length - 1; i >= 0; i--) {
      const el = this.elements[i]
      const { x, y } = el.position
      const { width, height } = el.size
      if (world.x >= x && world.x <= x + width && world.y >= y && world.y <= y + height) {
        if (!topmost) topmost = el
        if (!cctv && isCctvElement(el)) cctv = el
        if (!parking && isParkingElement(el)) parking = el
      }
    }
    const chosen = cctv ?? parking ?? topmost
    if (!chosen) return null
    return {
      id: chosen.id,
      name: elementName(chosen),
      isCctv: chosen === cctv,
      isParking: chosen === parking,
    }
  }

  /** 점유로 빨갛게 칠할 주차면 id 집합(만공차 체크 탭과 공유). */
  setOccupiedParking(ids: Iterable<string>): void {
    this.occupiedParking = new Set(ids)
    this.requestRender()
  }

  /** 포커스 지점(world 좌표) 설정 — 펄스 링 표시. null 이면 해제. */
  setFocusPoint(pt: { x: number; y: number } | null): void {
    this.focusPoint = pt
    this.requestRender()
  }

  /** 현재 줌을 유지한 채 world 좌표가 화면 중앙에 오도록 뷰포트를 이동한다. */
  centerOn(worldX: number, worldY: number): void {
    this.userInteracted = true
    const scale = this.viewportManager.getTransform().scale
    this.viewportManager.setTransform(
      this.canvas.width / 2 - worldX * scale,
      this.canvas.height / 2 - worldY * scale,
      scale,
    )
    this.requestRender()
  }

  /**
   * 한 객체를 화면 중앙에 크게 보여 주고 포커스 링을 띄운다.
   *
   * 객체 수정 시트에서 행을 고르면 "어떤 객체인지" 가 바로 보여야 한다.
   * CANVAS_EDITOR 모드에서는 JointJS paper 가 얼려져 숨어 있으므로 paper 를
   * 움직여도 화면은 그대로다 — 그 경로를 대신한다.
   *
   * 대상을 못 찾으면 false 를 돌려준다(호출 측이 기존 paper 경로로 폴백).
   */
  focusElement(id: string, options: { fillDivisor?: number; maxScale?: number } = {}): boolean {
    const el = this.elements.find(e => e.id === id)
    if (!el) return false
    this.userInteracted = true
    this.viewportManager.focusOnBounds(
      { x: el.position.x, y: el.position.y, width: el.size.width, height: el.size.height },
      options
    )
    this.focusPoint = {
      x: el.position.x + el.size.width / 2,
      y: el.position.y + el.size.height / 2,
    }
    this.requestRender()
    return true
  }

  /** 라벨(이름)·연결선 표시 토글 — 카테고리별. 전달된 값만 갱신. */
  setLabelVisibility(opts: { cctv?: boolean; parking?: boolean; links?: boolean }): void {
    if (typeof opts.cctv === 'boolean') this.showCctvNames = opts.cctv
    if (typeof opts.parking === 'boolean') this.showParkingNames = opts.parking
    if (typeof opts.links === 'boolean') this.showCctvLinks = opts.links
    this.requestRender()
  }

  /**
   * CCTV↔주차면 연결선 세그먼트를 elements 에서 다시 계산(캐시). CCTV 셀의
   * data.properties.linkedParkingLocationIds(주차면 cell id 배열) → 해당 주차면
   * 중심으로 선을 잇는다. pan/zoom 마다 재계산하지 않도록 캐시.
   */
  private recomputeCctvParkingLinks(): void {
    const centerById = new Map<string, { x: number; y: number }>()
    for (const el of this.elements) {
      centerById.set(el.id, {
        x: el.position.x + el.size.width / 2,
        y: el.position.y + el.size.height / 2,
      })
    }
    const segs: { x1: number; y1: number; x2: number; y2: number }[] = []
    for (const el of this.elements) {
      if (!isCctvElement(el)) continue
      const from = centerById.get(el.id)
      if (!from) continue
      const ids = (el.data as { properties?: { linkedParkingLocationIds?: unknown } })
        .properties?.linkedParkingLocationIds
      if (!Array.isArray(ids)) continue
      for (const pid of ids) {
        if (typeof pid !== 'string') continue
        const to = centerById.get(pid)
        if (!to) continue
        segs.push({ x1: from.x, y1: from.y, x2: to.x, y2: to.y })
      }
    }
    this.cctvParkingLinkSegments = segs
  }

  private nsId(id: string): string {
    return this.floorId ? `${this.floorId}::${id}` : id
  }

  private hasLighting(): boolean {
    return this.lightingBaseline || Object.keys(this.lightingStatuses).length > 0
  }

  /** 경광등(비상) 점멸 계수 — 약 0.7s 주기로 밝게(1)/어둡게(0.1) 토글. */
  private warningBlinkFactor(): number {
    const period = 700
    const phase = (performance.now() % period) / period
    return phase < 0.5 ? 1 : 0.1
  }

  private lightingFor(el: CanvasElement): LightingStatus | undefined {
    const name = elementName(el)
    return this.lightingStatuses[name] ?? this.lightingByNormalizedName[normalizeName(name)]
  }

  /**
   * Request render on next animation frame
   */
  private requestRender(): void {
    if (this.needsRender || this.destroyed) return

    this.needsRender = true
    this.animationFrameId = requestAnimationFrame(() => {
      // render() 안에서 다음 프레임을 예약할 수 있도록(비상 점멸 애니메이션 루프)
      // 플래그를 먼저 풀고 그린다 — 뒤에 풀면 render 중의 requestRender 가
      // 조기 반환되어 점멸이 한 프레임에서 멈춘다.
      this.needsRender = false
      this.render()
    })
  }

  /**
   * Main render loop
   */
  render(): void {
    const startTime = performance.now()
    // 라벨 대비는 배경 밝기에 달렸다 — 테마 토큰을 프레임마다 한 번만 해석한다
    // (resolveColor 는 캐시되고, 테마가 바뀌면 ThemeProvider 가 캐시를 비운다).
    this.bgIsDark = isDarkColor(resolveColor(CANVAS_BG_TOKEN) ?? '#ffffff')

    // Clear canvas
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)

    // Apply viewport transform
    const transform = this.viewportManager.getTransform()
    this.ctx.save()
    this.ctx.translate(transform.translate.x, transform.translate.y)
    this.ctx.scale(transform.scale, transform.scale)

    // Get visible elements (viewport culling)
    const visibleElements = this.options.enableCulling
      ? this.viewportManager.cullElements(this.elements)
      : this.elements

    if (this.options.debug) {
      log.debug(LogCategory.CANVAS, 'Rendering', { total: this.elements.length, visible: visibleElements.length, cullingEnabled: this.options.enableCulling })
    }

    // Render visible elements, collecting any selected ones for the highlight pass.
    const selectedVisible: CanvasElement[] = []
    const editTargetVisible: CanvasElement[] = []
    const unlinkedVisible: CanvasElement[] = []
    const autoLinkedVisible: CanvasElement[] = []
    const autoLinkedIds = this.autoLinkResult ? new Set(this.autoLinkResult.linkedIds) : null
    for (const element of visibleElements) {
      this.renderElement(element)
      if (this.selectedIds.has(element.id)) selectedVisible.push(element)
      if (this.editTargetIds.size > 0 && this.editTargetIds.has(element.id)) {
        editTargetVisible.push(element)
      }
      if (this.unlinkedTargetIds.size > 0 && this.unlinkedTargetIds.has(element.id)) {
        unlinkedVisible.push(element)
      }
      if (autoLinkedIds && autoLinkedIds.has(element.id)) autoLinkedVisible.push(element)
    }

    // Relationship-edit target outlines (green, clickable) — under the selection
    // highlight so the currently edited element stays visually distinct.
    if (editTargetVisible.length > 0) {
      this.renderEditTargetHighlight(editTargetVisible, transform.scale)
    }

    // CCTV↔주차면 연결선(항상 표시, selection 무관) — 씬 위, 관계/선택 오버레이 아래.
    if (this.showCctvLinks && this.cctvParkingLinkSegments.length > 0) {
      this.renderCctvParkingLinks(transform.scale)
    }

    // Relationship overlay (under the selection outline, above the scene).
    if (this.relationshipSegments.length > 0) {
      this.renderRelationshipOverlay(transform.scale)
    }

    // 자동 관계 생성 오버레이 — 반경 밖 타겟(빨강 점선) → 결과 연결선/강조 순.
    if (unlinkedVisible.length > 0) {
      this.renderOutlines(unlinkedVisible, '#EF4444', 1 / transform.scale, 0.6, [
        3 / transform.scale,
        3 / transform.scale,
      ])
    }
    if (this.autoLinkPreview) {
      this.renderAutoLinkPreview(transform.scale)
    }
    if (this.cctvRelationLines.length > 0) {
      this.renderCctvRelationLines(transform.scale)
    }
    if (this.drawingCheckMarkers.length > 0) {
      this.renderDrawingCheckMarkers(transform.scale)
    }
    if (this.autoLinkResult) {
      this.renderAutoLinkResultLines(transform.scale)
      if (autoLinkedVisible.length > 0) {
        this.renderOutlines(autoLinkedVisible, '#F59E0B', 3 / transform.scale, 1, [])
      }
    }

    // Selection highlight overlay (drawn on top, in world space).
    if (selectedVisible.length > 0) {
      this.renderSelectionHighlight(selectedVisible, transform.scale)
    }

    // 비상 호출 CCTV 의 연결 조명 점멸 — world 변환 안에서 조명 도형을 덧그린다.
    this.warningActive = false
    if (this.emergencyLightIds.size > 0) {
      const lightsVisible = visibleElements.filter(el => this.emergencyLightIds.has(el.id))
      if (lightsVisible.length > 0) {
        this.renderEmergencyLights(lightsVisible, transform.scale)
        this.warningActive = true
      }
    }

    this.ctx.restore()

    // CCTV 상태 오버레이(조명 bloom + 연결 끊김 배지) — world 변환을 벗어나
    // 스크린 좌표에서 그린다(줌과 무관하게 일정 크기, StatusOverlay 와 동일).
    if (this.hasLighting() || Object.keys(this.cctvStatuses).length > 0) {
      const cctvVisible = visibleElements.filter(isCctvElement)
      if (cctvVisible.length > 0) this.renderCctvOverlays(cctvVisible, transform)
    }
    // 포커스 펄스 링 — 시뮬레이터에서 선택된 객체 위치를 줌과 무관한 화면 크기로
    // 표시한다(스크린 좌표). 그려지는 동안 warningActive 로 애니메이션을 지속.
    if (this.focusPoint) {
      this.renderFocusRing(this.focusPoint, transform)
      this.warningActive = true
    }
    // 비상(경광등) 점멸이 그려졌으면 다음 프레임을 다시 예약해 애니메이션을 지속한다.
    // 경광등이 꺼지면 warningActive 가 false 로 남아 루프가 자연 종료된다.
    if (this.warningActive) this.requestRender()

    const renderTime = performance.now() - startTime
    if (this.options.debug) {
      log.debug(LogCategory.CANVAS, `Rendered in ${renderTime.toFixed(2)}ms`)
    }
  }

  /**
   * Pick the topmost element at a canvas-relative screen point.
   * Returns the cell id, or null on empty space.
   */
  hitTest(screenX: number, screenY: number): string | null {
    const world = this.viewportManager.screenToWorld(screenX, screenY)
    // 채움 없는 외곽선은 선 위 클릭만 히트 — 화면상 ~6px 이 잡히도록 월드 단위 환산
    const strokeTolerance = 6 / (this.viewportManager.getTransform().scale || 1)
    return hitTestElements(this.elements, world.x, world.y, strokeTolerance)
  }

  /** Set the highlighted selection (cell ids) and re-render. */
  setSelection(ids: Iterable<string>): void {
    this.selectedIds = new Set(ids)
    this.recomputeRelationships()
    this.requestRender()
  }

  /** Current selection as a new array. */
  getSelection(): string[] {
    return Array.from(this.selectedIds)
  }

  /** Set the relationship-edit target ids (green clickable outlines). Pass an
   *  empty iterable to clear when edit mode ends. */
  setEditTargetHighlights(ids: Iterable<string>): void {
    this.editTargetIds = new Set(ids)
    this.requestRender()
  }

  /** 자동 관계 생성 미리보기: 반경 밖이라 연결되지 않는 타겟 id(빨강 점선).
   *  빈 값으로 호출하면 지워진다. */
  setUnlinkedTargetHighlights(ids: Iterable<string>): void {
    this.unlinkedTargetIds = new Set(ids)
    this.requestRender()
  }

  /** 자동 관계 생성 미리보기(적용 전 후보). null 이면 지운다. */
  setAutoLinkPreviewOverlay(overlay: AutoLinkPreviewOverlay | null): void {
    this.autoLinkPreview = overlay
    this.requestRender()
  }

  /** 자동 관계 생성 결과 표시. null 이면 지운다. */
  setAutoLinkResultOverlay(overlay: AutoLinkResultOverlay | null): void {
    this.autoLinkResult = overlay
    this.requestRender()
  }

  /** CCTV 관계선(world 좌표). 빈 배열이면 지운다. */
  setCctvRelationLines(lines: AutoLinkPreviewOverlay['lines']): void {
    this.cctvRelationLines = lines
    this.requestRender()
  }

  /** 도면 대조 표시. 빈 배열이면 지운다. */
  setDrawingCheckMarkers(markers: DrawingCheckMarker[]): void {
    this.drawingCheckMarkers = markers
    this.requestRender()
  }

  /** Provide the template's relation config so the overlay can be drawn. */
  setRelationTypes(relationTypes: Record<string, TemplateRelationType> | undefined): void {
    this.relationTypes = relationTypes
    this.recomputeRelationships()
    this.requestRender()
  }

  /**
   * Recompute relationship overlay segments for the current selection. Cached so
   * pan/zoom (which re-render every frame) don't re-scan all elements; only
   * selection / element / relation-config changes trigger this.
   */
  private recomputeRelationships(): void {
    if (this.selectedIds.size === 0 || !this.relationTypes) {
      this.relationshipSegments = []
      return
    }

    const entities: RelationshipEntity[] = []
    const centerById = new Map<string, { x: number; y: number }>()
    for (const el of this.elements) {
      const props = (el.data as { properties?: Record<string, unknown> }).properties ?? {}
      entities.push({ id: el.id, properties: props })
      centerById.set(el.id, {
        x: el.position.x + el.size.width / 2,
        y: el.position.y + el.size.height / 2,
      })
    }

    const lines = collectRelationshipLinesFromEntities(this.selectedIds, entities, this.relationTypes)
    this.relationshipSegments = buildRelationshipSegments(lines, centerById)
  }

  /**
   * Draw relationship lines between related element centers. Colored per
   * relation key; bidirectional relations are dashed. Line width is divided by
   * scale for a constant ~2px on-screen stroke at any zoom.
   */
  private renderRelationshipOverlay(scale: number): void {
    this.ctx.save()
    this.ctx.globalAlpha = 0.85
    this.ctx.lineCap = 'round'
    const width = 2 / scale
    const dash = [width * 3, width * 1.5]
    for (const seg of this.relationshipSegments) {
      this.ctx.strokeStyle = seg.color
      this.ctx.lineWidth = width
      this.ctx.setLineDash(seg.bidirectional ? dash : [])
      this.ctx.beginPath()
      this.ctx.moveTo(seg.x1, seg.y1)
      this.ctx.lineTo(seg.x2, seg.y2)
      this.ctx.stroke()
    }
    this.ctx.restore()
  }

  /**
   * CCTV↔주차면 연결선. world 변환 안에서 그리되 선 두께는 scale 로 나눠 줌과
   * 무관하게 ~1px 로 유지. 관계 오버레이(파랑/주황)와 구분되게 청록 점선.
   */
  private renderCctvParkingLinks(scale: number): void {
    this.ctx.save()
    this.ctx.globalAlpha = 0.55
    this.ctx.strokeStyle = '#22d3ee'
    this.ctx.lineCap = 'round'
    const width = 1 / scale
    this.ctx.lineWidth = width
    this.ctx.setLineDash([width * 4, width * 3])
    this.ctx.beginPath()
    for (const s of this.cctvParkingLinkSegments) {
      this.ctx.moveTo(s.x1, s.y1)
      this.ctx.lineTo(s.x2, s.y2)
    }
    this.ctx.stroke()
    this.ctx.restore()
  }

  /**
   * Outline selected elements. Drawn inside the world transform, so line width
   * is divided by scale to keep a constant ~2px on screen at any zoom.
   */
  private renderSelectionHighlight(selected: CanvasElement[], scale: number): void {
    this.ctx.save()
    this.ctx.globalAlpha = 1
    this.ctx.strokeStyle = '#2196F3'
    this.ctx.lineWidth = 2 / scale
    this.ctx.setLineDash([6 / scale, 4 / scale])
    for (const el of selected) {
      this.ctx.strokeRect(el.position.x, el.position.y, el.size.width, el.size.height)
    }
    this.ctx.restore()
  }

  /**
   * Outline relationship-edit target elements — solid green, matching the paper
   * edit-mode highlight color (#10b981) so both renderers read the same way.
   */
  private renderEditTargetHighlight(targets: CanvasElement[], scale: number): void {
    this.ctx.save()
    this.ctx.globalAlpha = 1
    this.ctx.strokeStyle = '#10b981'
    this.ctx.lineWidth = 3 / scale
    for (const el of targets) {
      this.ctx.strokeRect(el.position.x, el.position.y, el.size.width, el.size.height)
    }
    this.ctx.restore()
  }

  /**
   * 자동 관계 생성 오버레이용 bbox 아웃라인. 선 두께/점선 간격은 호출부가 이미
   * scale 로 나눠 넘기므로 줌과 무관하게 화면 기준 두께를 유지한다.
   */
  private renderOutlines(
    els: CanvasElement[],
    color: string,
    lineWidth: number,
    alpha: number,
    dash: number[]
  ): void {
    this.ctx.save()
    this.ctx.globalAlpha = alpha
    this.ctx.strokeStyle = color
    this.ctx.lineWidth = lineWidth
    this.ctx.setLineDash(dash)
    for (const el of els) {
      this.ctx.strokeRect(el.position.x, el.position.y, el.size.width, el.size.height)
    }
    this.ctx.restore()
  }

  /** 자동 관계 생성 결과 연결선(소스 중심 → 타겟 중심), 관계별 색의 점선. */
  /**
   * 자동 관계 미리보기 — 후보 연결선 + 탐색 반경 원.
   *
   * 반경 원은 DOM 오버레이 시절(플래그 OFF 경로에 지금도 남아 있다)과 같은 무게로
   * 그린다: 옅은 채움 + 화면 기준 3px 실선 테두리 + 중심점. 캔버스로 옮기면서
   * 채움과 중심점이 빠지고 테두리도 알파 0.25 짜리 점선 하나만 남아, 객체가 빽빽한
   * 맵에서는 사실상 보이지 않았다(43b64c21 이 DOM 쪽에서 고쳤던 것과 같은 증상).
   * 두께·중심점 크기는 scale 로 나눠 줌과 무관하게 화면에서 일정하다 — 정규화된
   * 큰 좌표계에서는 scale 이 0.1 미만인 경우가 흔해 월드 상수 두께는 1px 아래로
   * 사라진다.
   */
  private renderAutoLinkPreview(scale: number): void {
    if (!this.autoLinkPreview) return
    const { lines, circles } = this.autoLinkPreview
    this.ctx.save()
    this.ctx.lineCap = 'round'
    this.ctx.setLineDash([])
    const border = 3 / scale
    const dot = 4 / scale
    for (const c of circles) {
      this.ctx.fillStyle = c.color
      this.ctx.strokeStyle = c.color
      this.ctx.lineWidth = border
      // 반경 안쪽을 옅게 채운다 — 테두리만으로는 어디가 반경인지 눈에 걸리지 않는다.
      // 원이 여러 개 겹쳐도 화면이 뭉개지지 않을 만큼만.
      this.ctx.globalAlpha = 0.12
      this.ctx.beginPath()
      this.ctx.arc(c.x, c.y, c.radius, 0, Math.PI * 2)
      this.ctx.fill()
      this.ctx.globalAlpha = 0.9
      this.ctx.stroke()
      // 중심점 — 어느 객체의 반경인지 원만 보고는 알 수 없다.
      this.ctx.beginPath()
      this.ctx.arc(c.x, c.y, dot, 0, Math.PI * 2)
      this.ctx.fill()
    }
    this.ctx.globalAlpha = 0.9
    this.ctx.lineWidth = 2 / scale
    for (const line of lines) {
      this.ctx.strokeStyle = line.color
      this.ctx.beginPath()
      this.ctx.moveTo(line.x1, line.y1)
      this.ctx.lineTo(line.x2, line.y2)
      this.ctx.stroke()
    }
    this.ctx.restore()
  }

  /** CCTV 관계선 — 화면 두께 3px 고정(줌과 무관하게 클릭할 만한 굵기). */
  private renderCctvRelationLines(scale: number): void {
    this.ctx.save()
    this.ctx.globalAlpha = 0.9
    this.ctx.lineCap = 'round'
    this.ctx.setLineDash([])
    this.ctx.lineWidth = 3 / scale
    for (const line of this.cctvRelationLines) {
      this.ctx.strokeStyle = line.color
      this.ctx.beginPath()
      this.ctx.moveTo(line.x1, line.y1)
      this.ctx.lineTo(line.x2, line.y2)
      this.ctx.stroke()
    }
    this.ctx.restore()
  }

  /** 도면 대조 표시 — 화면 크기 고정 고리(지름 20px)+가운데 점. 객체가 빽빽해도 눈에 걸리게. */
  private renderDrawingCheckMarkers(scale: number): void {
    this.ctx.save()
    this.ctx.setLineDash([])
    this.ctx.globalAlpha = 0.95
    this.ctx.lineWidth = 3 / scale
    for (const m of this.drawingCheckMarkers) {
      this.ctx.strokeStyle = m.color
      this.ctx.fillStyle = m.color
      this.ctx.beginPath()
      this.ctx.arc(m.x, m.y, 10 / scale, 0, Math.PI * 2)
      this.ctx.stroke()
      this.ctx.beginPath()
      this.ctx.arc(m.x, m.y, 2.5 / scale, 0, Math.PI * 2)
      this.ctx.fill()
    }
    this.ctx.restore()
  }

  private renderAutoLinkResultLines(scale: number): void {
    if (!this.autoLinkResult) return
    this.ctx.save()
    this.ctx.globalAlpha = 0.9
    this.ctx.lineCap = 'round'
    const width = 1.5 / scale
    this.ctx.lineWidth = width
    this.ctx.setLineDash([width * 4, width * 3])
    for (const line of this.autoLinkResult.lines) {
      this.ctx.strokeStyle = line.color
      this.ctx.beginPath()
      this.ctx.moveTo(line.x1, line.y1)
      this.ctx.lineTo(line.x2, line.y2)
      this.ctx.stroke()
    }
    this.ctx.restore()
  }

  /**
   * 비상 호출 CCTV 에 연결된 조명 점멸 — 조명 도형(path 우선, 없으면 bbox)을
   * 빨간색으로 덧그리고 warningBlinkFactor 로 투명도를 깜빡인다. 경광등 bloom 과
   * 같은 주기라 CCTV 점멸과 동기화되어 보인다.
   */
  private renderEmergencyLights(lights: CanvasElement[], scale: number): void {
    const blink = this.warningBlinkFactor()
    this.ctx.save()
    this.ctx.globalAlpha = Math.max(0.15, blink)
    this.ctx.strokeStyle = '#ef4444'
    this.ctx.lineWidth = 3 / scale
    this.ctx.lineCap = 'round'
    for (const el of lights) {
      const d = el.attrs.body?.d
      this.ctx.save()
      this.ctx.translate(el.position.x, el.position.y)
      if (d) {
        this.ctx.stroke(new Path2D(d))
      } else {
        this.ctx.strokeRect(0, 0, el.size.width, el.size.height)
      }
      this.ctx.restore()
    }
    this.ctx.restore()
  }

  /**
   * 포커스 펄스 링 — 약 1.2s 주기로 안쪽에서 바깥으로 퍼지며 사라지는 두 겹 원.
   * 스크린 좌표로 그려 줌 레벨과 무관하게 항상 같은 크기로 보인다.
   */
  private renderFocusRing(pt: { x: number; y: number }, transform: Transform): void {
    const cx = pt.x * transform.scale + transform.translate.x
    const cy = pt.y * transform.scale + transform.translate.y
    const period = 1200
    const phase = (performance.now() % period) / period
    this.ctx.save()
    // 바깥으로 퍼지는 링
    const radius = 10 + 26 * phase
    this.ctx.globalAlpha = 0.9 * (1 - phase)
    this.ctx.strokeStyle = '#22d3ee'
    this.ctx.lineWidth = 3
    this.ctx.beginPath()
    this.ctx.arc(cx, cy, radius, 0, Math.PI * 2)
    this.ctx.stroke()
    // 고정 코어 링 — 펄스가 사라지는 순간에도 위치가 유지돼 보이게.
    this.ctx.globalAlpha = 0.95
    this.ctx.lineWidth = 2
    this.ctx.beginPath()
    this.ctx.arc(cx, cy, 9, 0, Math.PI * 2)
    this.ctx.stroke()
    this.ctx.restore()
  }

  /**
   * Render individual element.
   *
   * Draws every present part of the cell — body (path OR rect), image, and a
   * centered label — instead of one mutually-exclusive branch. All primitives
   * are drawn in the cell's local coordinate space (translated by position
   * once), because the editor stores path `d` and label anchors relative to the
   * cell position, not in absolute world coords.
   */
  private renderElement(element: CanvasElement): void {
    const plan = planElementRender(element)

    // 주차면 점유 시 채움색을 빨강으로 오버라이드. 점유 소스는 (1) 실시간
    // parkingStatuses(P2 피드) 또는 (2) 만공차 체크 탭의 수동 점유(occupiedParking).
    let fillOverride: string | undefined
    if (isParkingElement(element)) {
      const occupied =
        this.occupiedParking.has(element.id) ||
        !!this.parkingStatuses[this.nsId(element.id)]?.occupied
      if (occupied) fillOverride = '#ef4444'
    }

    this.ctx.save()
    this.ctx.translate(plan.position.x, plan.position.y)
    this.ctx.globalAlpha = plan.opacity

    // 1. Body — path (SVG d) or plain rectangle (size).
    // 색은 resolveColor 로 var(--token)·named 까지 실제 rgb 로 해석(미해석 시 검정 칠).
    if (plan.path) {
      renderSVGPath(
        this.ctx,
        plan.path.d,
        resolveColor(fillOverride ?? plan.path.fill),
        resolveColor(plan.path.stroke),
        plan.path.strokeWidth
      )
    } else if (plan.polygon) {
      // standard.Polygon (구역) — refPoints 정점 다각형.
      const { points, strokeWidth } = plan.polygon
      this.ctx.beginPath()
      points.forEach((p, i) => (i === 0 ? this.ctx.moveTo(p.x, p.y) : this.ctx.lineTo(p.x, p.y)))
      this.ctx.closePath()
      const fill = resolveColor(fillOverride ?? plan.polygon.fill)
      if (fill) {
        this.ctx.fillStyle = fill
        this.ctx.fill()
      }
      const stroke = resolveColor(plan.polygon.stroke)
      if (stroke) {
        this.ctx.strokeStyle = stroke
        this.ctx.lineWidth = strokeWidth
        this.ctx.stroke()
      }
    } else if (plan.rect) {
      const { width, height, strokeWidth } = plan.rect
      const fill = resolveColor(fillOverride ?? plan.rect.fill)
      if (fill) {
        this.ctx.fillStyle = fill
        this.ctx.fillRect(0, 0, width, height)
      }
      const stroke = resolveColor(plan.rect.stroke)
      if (stroke) {
        this.ctx.strokeStyle = stroke
        this.ctx.lineWidth = strokeWidth
        this.ctx.strokeRect(0, 0, width, height)
      }
    }

    // 2. Image icon (CCTV / light / object asset), cached + drawn once decoded.
    if (plan.image) {
      const img = this.getCachedImage(plan.image.href)
      if (img && img.complete && img.naturalWidth > 0) {
        this.ctx.drawImage(img, 0, 0, plan.image.width, plan.image.height)
      }
    }

    // 3. Centered label — 카테고리별 표시 토글.
    //  · CCTV 이름: showCctvNames (기본 ON)
    //  · 주차면 이름: showParkingNames (기본 OFF) — 라벨이 비어 있어도 properties.name 으로 그림
    //  · 그 외(구역/동 등): 기존 라벨 그대로 표시
    let text = plan.label?.text
    let fontSize = plan.label?.fontSize ?? 12
    const fontFamily = plan.label?.fontFamily ?? 'Arial'
    const fill = plan.label
      ? (resolveColor(plan.label.fill) ?? plan.label.fill)
      : this.bgIsDark
        ? '#ffffff'
        : '#0f172a'
    const lcx = plan.label?.cx ?? element.size.width / 2
    const lcy = plan.label?.cy ?? element.size.height / 2
    let deviceLabel = false
    if (isCctvElement(element)) {
      if (!this.showCctvNames) text = undefined
      else {
        text = text || elementName(element)
        deviceLabel = true
        // CCTV 이름 폰트 — 저장 라벨 크기의 0.25배(직전 화면 대비 다시 절반).
        fontSize = (plan.label?.fontSize ?? 12) * 0.25
      }
    } else if (isParkingElement(element)) {
      if (!this.showParkingNames) text = undefined
      else {
        text = text || elementName(element)
        deviceLabel = true
        // "parkingLocations-880" 처럼 prefix 가 붙은 이름은 뒤 숫자만 표시.
        const m = /parkinglocations[^0-9]*(\d+)/i.exec(text)
        if (m) text = m[1]
        // CCTV 이름과 동일한 폰트 크기(요청). 셀 크기 비례 fit 을 쓰지 않으므로
        // 셀마다 크기가 달라지던 문제도 사라지고 CCTV 와 균일하게 보인다.
        fontSize = (plan.label?.fontSize ?? 12) * 0.25
      }
    } else if (isBellElement(element) && text) {
      // 비상벨 라벨(시뮬레이터가 끝 숫자를 주입) — CCTV 이름과 같은 크기로 통일.
      deviceLabel = true
      fontSize = (plan.label?.fontSize ?? 12) * 0.25
    }
    if (text) {
      const scale = this.viewportManager.getTransform().scale || 1
      // 디바이스(CCTV/주차면/비상벨) 이름 LOD — 아이콘이 화면에서 점 수준
      // (12px 미만)인 축소 줌에서는 이름을 생략한다. 수백 개 라벨이 겹쳐
      // 뭉개지는 것을 막고, 확대하면 아래 최소 크기 클램프로 바로 읽힌다.
      if (deviceLabel && element.size.width * scale < MIN_DEVICE_ICON_SCREEN_PX) {
        text = undefined
      }
      if (text) {
        // 라벨 가독성 보장 — 월드 좌표 폰트는 축소 줌에서 화면상 1px 미만이 되어
        // 전혀 안 보였다(검은 글자로 오인된 원인). 화면 기준 최소 크기로 클램프하고
        // 어두운 halo 를 깔아 밝은 도면 선 위에서도 읽히게 한다.
        if (fontSize * scale < MIN_LABEL_SCREEN_PX) fontSize = MIN_LABEL_SCREEN_PX / scale
        this.ctx.globalAlpha = 1
        this.ctx.font = `${fontSize}px ${fontFamily}`
        this.ctx.textAlign = 'center'
        this.ctx.textBaseline = 'middle'
        // halo 는 배경과 같은 계열이어야 글자가 떠 보인다.
        this.ctx.strokeStyle = this.bgIsDark ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.85)'
        this.ctx.lineWidth = Math.max(2 / scale, fontSize * 0.12)
        this.ctx.lineJoin = 'round'
        this.ctx.strokeText(text, lcx, lcy)
        // 배경과 같은 계열의 라벨색은 데이터가 어떻든 안 읽힌다 — 반대색으로 대체.
        this.ctx.fillStyle =
          this.bgIsDark === isDarkColor(fill) ? (this.bgIsDark ? '#ffffff' : '#0f172a') : fill
        this.ctx.fillText(text, lcx, lcy)
      }
    }

    this.ctx.restore()
  }

  /**
   * CCTV 상태 오버레이를 스크린 좌표로 그린다.
   *  · 조명 bloom: 종류=색, 밝기(dimming)=번짐 크기·농도. baseline ON 이면 통로등을 유휴 20%로.
   *  · 연결 끊김: 아이콘 우상단에 빨강 '!' 배지.
   * 모두 StatusOverlay 의 수식/색을 그대로 옮긴 것이라 시각 결과가 동일하다.
   */
  private renderCctvOverlays(els: CanvasElement[], transform: Transform): void {
    const { scale } = transform
    const tx = transform.translate.x
    const ty = transform.translate.y
    const lighting = this.hasLighting()
    const hasCctv = Object.keys(this.cctvStatuses).length > 0

    this.ctx.save()
    for (const el of els) {
      const w = el.size.width
      const h = el.size.height
      const cx = (el.position.x + w / 2) * scale + tx
      const cy = (el.position.y + h / 2) * scale + ty

      if (lighting) {
        const ls = this.lightingFor(el)
        const active = !!(ls && ls.on)
        if (active || this.lightingBaseline) {
          const lightType = active ? ls!.lightType : '통로등'
          const dimming = active ? ls!.dimming : BASELINE_DIM
          const [r, g, b] = lightGlowRgb(lightType)
          const d = Math.min(1, Math.max(0, dimming / 100))
          // 경광등(비상)이거나 비상 호출 중인 CCTV 면 계속 점멸한다 — 현재 프레임의
          // 점멸 계수로 농도를 변조하고 warningActive 를 세워 render() 가 다음 프레임을
          // 다시 그리게 한다(비상벨 호출 → 디밍이 lightType 무관하게 깜빡임).
          const name = elementName(el)
          const isEmergency =
            lightType.includes('경광') ||
            this.emergencyCctvs.has(name) ||
            this.emergencyCctvs.has(normalizeName(name))
          const blink = isEmergency ? this.warningBlinkFactor() : 1
          if (isEmergency) this.warningActive = true
          const iconPx = Math.max(w, h, 24) * scale
          const radius = (iconPx * (1.3 + 2.6 * d)) / 2
          const core = Math.min(1, 0.55 + 0.45 * d) * blink
          const mid = (0.3 + 0.5 * d) * blink
          const grad = this.ctx.createRadialGradient(cx, cy, 0, cx, cy, radius)
          grad.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${core})`)
          grad.addColorStop(0.32, `rgba(${r}, ${g}, ${b}, ${mid})`)
          grad.addColorStop(0.7, `rgba(${r}, ${g}, ${b}, 0)`)
          this.ctx.fillStyle = grad
          this.ctx.beginPath()
          this.ctx.arc(cx, cy, radius, 0, Math.PI * 2)
          this.ctx.fill()
        }
      }

      if (hasCctv) {
        const st = this.cctvStatuses[this.nsId(el.id)]
        if (st && !st.connected) {
          const bx = (el.position.x + w) * scale + tx
          const by = el.position.y * scale + ty
          this.ctx.beginPath()
          this.ctx.arc(bx, by, 8, 0, Math.PI * 2)
          this.ctx.fillStyle = '#ef4444'
          this.ctx.fill()
          this.ctx.fillStyle = '#fff'
          this.ctx.font = 'bold 11px sans-serif'
          this.ctx.textAlign = 'center'
          this.ctx.textBaseline = 'middle'
          this.ctx.fillText('!', bx, by)
        }
      }
    }
    this.ctx.restore()
  }

  /**
   * Return a cached <img> for href, creating + loading it on first request.
   * Triggers a re-render once the image finishes decoding.
   */
  private getCachedImage(href: string): HTMLImageElement {
    const cached = this.imageCache.get(href)
    if (cached) return cached

    const img = new Image()
    img.onload = () => this.requestRender()
    img.src = href
    this.imageCache.set(href, img)
    return img
  }

  /**
   * Pan viewport
   */
  pan(dx: number, dy: number): void {
    this.userInteracted = true
    this.viewportManager.applyTransform(dx, dy)
    this.requestRender()
  }

  /**
   * Zoom viewport
   */
  zoom(scale: number, centerX?: number, centerY?: number): void {
    this.userInteracted = true
    if (centerX !== undefined && centerY !== undefined) {
      // Zoom to point
      const worldPoint = this.viewportManager.screenToWorld(centerX, centerY)
      const newTransform = {
        translate: {
          x: centerX - worldPoint.x * scale,
          y: centerY - worldPoint.y * scale,
        },
        scale,
      }
      this.viewportManager.setTransform(
        newTransform.translate.x,
        newTransform.translate.y,
        scale
      )
    } else {
      // Zoom to center
      this.viewportManager.applyTransform(0, 0, scale)
    }

    this.requestRender()
  }

  /**
   * Resize canvas
   */
  resize(width: number, height: number): void {
    this.canvas.width = width
    this.canvas.height = height
    this.viewportManager.updateDimensions(width, height)
    // 사용자가 아직 줌/팬 하지 않았으면 새 크기에 맞춰 다시 fit(레이아웃 정착 중
    // 여러 번 발화해도 최종 크기로 수렴). 사용자 조작 후엔 뷰포트를 보존한다.
    if (!this.userInteracted && this.elements.length > 0) {
      this.fitToContent()
    }
    this.requestRender()
  }

  /**
   * Destroy renderer
   */
  destroy(): void {
    this.destroyed = true
    this.unbindFromGraph()
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId)
      this.animationFrameId = null
    }
    this.elements = []
    this.imageCache.clear()
    log.debug(LogCategory.CANVAS, 'Destroyed')
  }
}
